import React, { useState, useRef, useEffect } from "react";
import { API_URLS } from "../../apiUrl";
import * as CryptoJS from "crypto-js";
import BoroughDropdown from "../customHooks/boroughDropdown";
import WardDropdown from "../customHooks/wardDropdown";
import StreetDropdown from "../customHooks/streetdropdown";
import axiosInstance from "../../components/axiosInstance";

type FormData = {
  survyer_name: string;
  login_user_name: string;
  password: string;
  borough_ids: string;
  ward_ids: string;
  street_ids: string;
  municipality_id: any;
  added_by: any;
};

const AdddSurveyer = ({ muniCipalityName, municipalityId, roleId }: any) => {
  const [formData, setFormData] = useState<FormData>({
    survyer_name: "",
    login_user_name: "",
    password: "",
    borough_ids: "",
    ward_ids: "",
    street_ids: "",
    municipality_id: municipalityId,
    added_by: roleId
  });

  const [errors, setErrors] = useState<any>({});
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [dataExistMessage, setDataExistMessage] = useState<string>("");
  const [dataFailedMessage, setDataFailedMessage] = useState<string>("");

  const formRef = useRef<HTMLFormElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = (): boolean => {
    const newErrors: any = {};
    
    if (!formData.survyer_name.trim()) {
      newErrors.survyer_name = "Surveyor name is required";
    } else if (!/^[A-Za-z\s]+$/.test(formData.survyer_name)) {
      newErrors.survyer_name = "Surveyor name must contain only letters and spaces";
    }

    if (!formData.login_user_name.trim()) {
      newErrors.login_user_name = "User name is required";
    } else if (!/^[A-Za-z0-9@.]+$/.test(formData.login_user_name)) {
      newErrors.login_user_name = "User name must contain only alphanumeric characters, '@', and '.'";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else {
      const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[A-Za-z0-9!@#$%^&*]{5,}$/;
      if (!passwordRegex.test(formData.password)) {
        newErrors.password = 'Password must be at least 5 characters long, contain at least one special character, and one number';
      }
    }

    if (!formData.borough_ids.trim()) {
      newErrors.borough_ids = "Borough is required";
    }

    if (!formData.ward_ids.trim()) {
      newErrors.ward_ids = "Ward is required";
    }

    if (!formData.street_ids.trim()) {
      newErrors.street_ids = "Street is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      survyer_name: "",
      login_user_name: "",
      password: "",
      borough_ids: "",
      ward_ids: "",
      street_ids: "",
      municipality_id: municipalityId,
      added_by: roleId
    });
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      const hashedPassword = CryptoJS.MD5(formData.password).toString();
      const updatedFormData = { ...formData, password: hashedPassword, added_by: roleId };

      try {
        const response = await axiosInstance.post(`${API_URLS.AddSurveyor}`, updatedFormData);

        if (response.data.data.message) {
          resetForm();
          setDataExistMessage(`${response.data.data.message}`);
          setTimeout(() => setDataExistMessage(""), 5000);
        } else {
          resetForm();
          setSuccessMessage(`${response.data.message}`);
          setTimeout(() => setSuccessMessage(""), 5000);
        }

        setErrors({});
      } catch (error) {
        setDataFailedMessage("Surveyor Add failed.");
        setTimeout(() => setDataFailedMessage(""), 5000);
      }
    }
  };

  const dropdownRef = useRef<any>(null);
  const dropdownRefWard = useRef<any>(null);
  const dropdownRefStreet = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpenBorough(false);
      }
      if (dropdownRefWard.current && !dropdownRefWard.current.contains(event.target)) {
        setIsDropdownOpenWard(false);
      }
      if (dropdownRefStreet.current && !dropdownRefStreet.current.contains(event.target)) {
        setIsDropdownOpenStreet(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [
    setIsDropdownOpenBorough,
    isDropdownOpenBorough,
    ,
    boroughList,
    loadingBoroughList,
  ] = BoroughDropdown(municipalityId);

  const [
    setIsDropdownOpenWard,
    isDropdownOpenWard,
    ,
    wardList,
    loadingWardList,
  ] = WardDropdown(municipalityId, formData.borough_ids);

  const [
    setIsDropdownOpenStreet,
    isDropdownOpenStreet,
    ,
    streetList,
    loadingStreetList,
  ] = StreetDropdown(municipalityId, formData.ward_ids);

  const toggleDropdownBorough = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDropdownOpenBorough((prev: any) => !prev);
  };

  const toggleDropdownWard = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDropdownOpenWard((prev: any) => !prev);
  };

  const toggleDropdownStreets = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDropdownOpenStreet((prev: any) => !prev);
  };

  const handleBoroughSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData((prevFormData) => {
      const newSelections = prevFormData.borough_ids ? prevFormData.borough_ids.split(",") : [];

      if (checked) {
        newSelections.push(value);
      } else {
        const index = newSelections.indexOf(value);
        if (index > -1) {
          newSelections.splice(index, 1);
        }
      }

      return {
        ...prevFormData,
        borough_ids: newSelections.join(","),
      };
    });
  };

  const handleWardSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData((prevFormData) => {
      const newSelections = prevFormData.ward_ids ? prevFormData.ward_ids.split(",") : [];

      if (checked) {
        if (!newSelections.includes(value)) {
          newSelections.push(value);
        }
      } else {
        const index = newSelections.indexOf(value);
        if (index > -1) {
          newSelections.splice(index, 1);
        }
      }

      return {
        ...prevFormData,
        ward_ids: newSelections.join(","),
      };
    });
  };

  const handleStreetSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setFormData((prevFormData) => {
      const newSelections = prevFormData.street_ids ? prevFormData.street_ids.split(",") : [];

      if (checked) {
        if (!newSelections.includes(value)) {
          newSelections.push(value);
        }
      } else {
        const index = newSelections.indexOf(value);
        if (index > -1) {
          newSelections.splice(index, 1);
        }
      }

      return {
        ...prevFormData,
        street_ids: newSelections.join(","),
      };
    });
  };

  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allStreetIds = streetList.map((street: any) => street.street_primary_id.toString()).join(",");
      setFormData({ ...formData, street_ids: allStreetIds });
    } else {
      setFormData({ ...formData, street_ids: "" });
    }
  };

  useEffect(() => {
    if (streetList.length > 0) {
      const selectedStreetIds = formData.street_ids.split(",").filter(Boolean);
      setSelectAll(streetList.every((street: any) => selectedStreetIds.includes(street.street_primary_id.toString())));
    }
  }, [formData.street_ids, streetList]);

  return (
    <div className="max-w-full mx-auto">
      <div className="flex flex-col gap-5">
        <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Add Surveyor</h1>
        <div className="p-6 bg-white rounded-lg shadow-lg dark:bg-gray-800">
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
              <div>
                <label htmlFor="survyer_name" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Surveyor Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="survyer_name"
                  name="survyer_name"
                  value={formData.survyer_name}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                />
                {errors.survyer_name && (
                  <p className="text-sm text-red-600 mt-1">{errors.survyer_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="login_user_name" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Login User Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="login_user_name"
                  name="login_user_name"
                  value={formData.login_user_name}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                />
                {errors.login_user_name && (
                  <p className="text-sm text-red-600 mt-1">{errors.login_user_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Password <span className="text-red-600">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder='E.g. Test@1234'
                />
                {errors.password && (
                  <p className="text-sm text-red-600 mt-1">{errors.password}</p>
                )}
              </div>

              <div className="relative z-[4]">
                <label htmlFor="borough_ids" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Borough <span className="text-red-600">*</span>
                </label>
                <div className="relative z-[4]" ref={dropdownRef}>
                  <button
                    onClick={toggleDropdownBorough}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {formData.borough_ids && formData.borough_ids.length > 0
                      ? boroughList
                        .filter((borough: any) => formData.borough_ids.split(",").includes(borough.borough_primary_id.toString()))
                        .map((borough: any) => borough.borough_name)
                        .join(", ")
                      : "Select Borough"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenBorough && (
                    <div className="absolute top-full w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 max-h-[200px] overflow-y-auto">
                      {boroughList.length === 0 ? (
                        <p className="text-sm text-red-600 dark:text-white">Borough not found!</p>
                      ) : (
                        boroughList.map((borough: any) => (
                          <div key={borough.borough_primary_id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`borough_${borough.borough_primary_id}`}
                              value={borough.borough_primary_id}
                              checked={formData.borough_ids.split(",").includes(borough.borough_primary_id.toString())}
                              onChange={handleBoroughSelect}
                              className="text-blue-600"
                            />
                            <label htmlFor={`borough_${borough.borough_primary_id}`} className="text-sm text-gray-900 dark:text-white">
                              {borough.borough_name}
                            </label>
                          </div>
                        ))
                      )}
                      {loadingBoroughList && <p>Loading...</p>}
                    </div>
                  )}
                </div>
                {errors.borough_ids && (
                  <p className="text-red-600 text-sm mt-1">{errors.borough_ids}</p>
                )}
              </div>

              <div>
                <label htmlFor="ward_tbl_id" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Ward <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefWard}>
                  <button
                    onClick={toggleDropdownWard}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {formData.ward_ids && formData.ward_ids.length > 0
                      ? wardList
                        .filter((ward: any) => formData.ward_ids.split(",").includes(ward.ward_primary_id.toString()))
                        .map((ward: any) => ward.ward_no)
                        .join(", ")
                      : "Select Ward"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenWard && (
                    <div className="absolute top-full w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 max-h-[200px] overflow-y-auto">
                      {wardList.length === 0 ? (
                        <p className="text-sm text-red-600 dark:text-white">Ward not found!</p>
                      ) : (
                        wardList.map((ward: any) => (
                          <div key={ward.ward_primary_id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`ward_${ward.ward_primary_id}`}
                              value={ward.ward_primary_id}
                              checked={formData.ward_ids.split(",").includes(ward.ward_primary_id.toString())}
                              onChange={handleWardSelect}
                              className="text-blue-600"
                            />
                            <label htmlFor={`ward_${ward.ward_primary_id}`} className="text-sm text-gray-900 dark:text-white">
                              {ward.ward_no}
                            </label>
                          </div>
                        ))
                      )}
                      {loadingWardList && <p>Loading...</p>}
                    </div>
                  )}
                </div>
                {errors.ward_ids && (
                  <p className="text-red-600 text-sm mt-1">{errors.ward_ids}</p>
                )}
              </div>

              <div>
                <label htmlFor="street_ids" className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                  Streets <span className="text-red-600">*</span>
                </label>
                <div className="relative" ref={dropdownRefStreet}>
                  <button
                    onClick={toggleDropdownStreets}
                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center"
                    type="button"
                  >
                    {formData.street_ids && formData.street_ids.length > 0
                      ? streetList
                        .filter((street: any) => formData.street_ids.split(",").includes(street.street_primary_id.toString()))
                        .map((street: any) => street.street_name)
                        .join(", ")
                      : "Select Street"}
                    <i className="ri-arrow-down-s-line ri-lg ml-auto"></i>
                  </button>

                  {isDropdownOpenStreet && (
                    <div className="absolute top-full w-full bg-gray-50 border border-gray-300 rounded-lg p-2.5 dark:bg-gray-700 dark:border-gray-600 max-h-[200px] overflow-y-auto">
                      {streetList.length === 0 ? (
                        <p className="text-sm text-red-600 dark:text-white">Street not found!</p>
                      ) : (
                        <>
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="checkbox"
                              id="select_all_streets"
                              checked={selectAll}
                              onChange={handleSelectAll}
                              className="text-blue-600"
                            />
                            <label htmlFor="select_all_streets" className="text-sm font-medium text-gray-900 dark:text-white">
                              Select All
                            </label>
                          </div>
                          {streetList.map((street: any) => (
                            <div key={street.street_primary_id} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`street_${street.street_primary_id}`}
                                value={street.street_primary_id}
                                checked={formData.street_ids.split(",").includes(street.street_primary_id.toString())}
                                onChange={handleStreetSelect}
                                className="text-blue-600"
                              />
                              <label htmlFor={`street_${street.street_primary_id}`} className="text-sm text-gray-900 dark:text-white">
                                {street.street_name}
                              </label>
                            </div>
                          ))}
                        </>
                      )}
                      {loadingStreetList && <p>Loading...</p>}
                    </div>
                  )}
                </div>
                {errors.street_ids && (
                  <p className="text-red-600 text-sm mt-1">{errors.street_ids}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
                  Add Surveyor
                </button>
              </div>
            </div>
          </form>

          {successMessage && (
            <div className="mt-4 p-4 bg-green-600 text-white rounded-lg flex justify-between items-center">
              <span>{successMessage}</span>
              <button
                onClick={() => setSuccessMessage('')}
                className="ml-4 text-white font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}

          {dataExistMessage && (
            <div className="mt-4 p-4 bg-yellow-500 text-black rounded-lg flex justify-between items-center">
              <span>{dataExistMessage}</span>
              <button
                onClick={() => setDataExistMessage('')}
                className="ml-4 text-black font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}

          {dataFailedMessage && (
            <div className="mt-4 p-4 bg-red-500 text-black rounded-lg flex justify-between items-center">
              <span>{dataFailedMessage}</span>
              <button
                onClick={() => setDataFailedMessage('')}
                className="ml-4 text-black font-bold text-lg focus:outline-none"
              >
                &times;
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdddSurveyer;